/**
 * Papéis conhecidos da plataforma. Correspondem a `perkKey`s resolvidos pelo
 * backend e devolvidos no sign-in (`platformProfile.perks[].perkKey`). São
 * strings (decisão: papéis identificados por chave, não por UUID). Renomeados
 * de PRIMUM_ADMIN/IMPERSONATOR para o formato `{plataforma}.{roleCamelCase}`
 * na migração `20260715120000-perk-taxonomy-refactor` do monolith (mesmo
 * `_id`/grants — só a key mudou).
 *
 * Hierarquia no front (decisão local, ainda não espelhada 1:1 no backend):
 *  - SYSTEM_ADMINISTRATOR → super-admin técnico (curinga): satisfaz qualquer
 *    checagem de papel. **No backend os curingas são dois**, SYSTEM_ADMINISTRATOR
 *    e ADMINISTRATOR (`WILDCARD_PERK_KEYS`, em `role-check.ts` do monolith):
 *    os dois passam em qualquer `@Roles`, e só o `@StrictRoles` os ignora. Aqui
 *    o curinga é só o SYSTEM_ADMINISTRATOR, porque o plano é ADMINISTRATOR ter
 *    menos privilégios — então um gate de UX que espelha uma rota `@Roles` lista
 *    ADMINISTRATOR explicitamente.
 *  - ADMINISTRATOR → papel de módulo "admin geral": hoje é exigido
 *    explicitamente por quase todo `@Roles(...)` do backend.
 *  - demais papéis de módulo (ex.: CUSTOMER_SUCCESS_TEAM) → liberam
 *    módulos/rotas específicas.
 *
 * Para adicionar um papel: crie a constante, inclua na união `Role` e use no
 * `RoleGuard` da rota / no `roles` do item de menu.
 */
export const ADMINISTRATOR = 'adminDashboard.administrator';
export const SYSTEM_ADMINISTRATOR = 'adminDashboard.systemAdministrator';
export const CUSTOMER_SUCCESS_TEAM = 'CUSTOMER_SUCCESS_TEAM';

/**
 * Editor de acessos (migração `20260904140000-seed-iam-editor-roles` do
 * monolith): gerencia as roles/features de um perfil que **já existe**, sem ser
 * admin geral. Vê o módulo de Usuários (lista + detalhe, onde mora o modal de
 * acessos) e nada mais — em particular **não** entra em `CATALOG_ROLES`: ele lê
 * o catálogo pelo endpoint, para popular a lista do modal, mas não tem a tela.
 *
 * O que ele consegue conceder de fato não se decide aqui nem no `@Roles` do
 * backend: é o `grantableByPerkKeys` de cada perk-alvo, comparado literalmente.
 *
 * A capacidade de **ler pessoa** que o backend exige nesses endpoints
 * (`identity.viewer`) chega a ele por `grantsPerkKeys` e volta resolvida nos
 * `perks` do sign-in — não há constante para ela aqui porque nenhuma tela
 * gateia por ela.
 */
export const IAM_EDITOR = 'adminDashboard.iamEditor';

/**
 * Espelha o `assertCanGrant` do back (`perk-admin.controller.ts`): um perk é
 * concedível pelo admin logado se **não restringe** (`grantableByPerkKeys`
 * vazio) ou se ele **possui** uma das keys exigidas. Comparação **literal, sem
 * curinga** — o back não excetua `systemAdministrator` aqui, então o front
 * também não, para a lista casar com o que a concessão de fato aceita.
 *
 * É só filtro de UX (não mostrar o que daria 403); a barreira que vale é a do
 * back. Ver a tela `Vantagens` do perfil (`ProfilePerksEditorModal`).
 */
export function canGrantPerk(
  grantableByPerkKeys: string[],
  holderPerkKeys: readonly string[],
): boolean {
  if (grantableByPerkKeys.length === 0) return true;
  return grantableByPerkKeys.some((key) => holderPerkKeys.includes(key));
}

/**
 * Papéis do Integrador TOTVS (cadastrados na migração
 * `20260723120000-seed-totvs-integrator-roles` do monolith). Liberam o menu/telas
 * do integrador junto com os dois admins principais (SYSTEM_ADMINISTRATOR curinga
 * + ADMINISTRATOR). O `_ADMIN` pode executar ações; o `_VIEWER` só visualiza — o
 * gate de ações de escrita nas páginas usa `TOTVS_INTEGRATOR_WRITE_ROLES`.
 */
export const TOTVS_INTEGRATOR_ADMIN = 'adminDashboard.totvsIntegratorAdmin';
export const TOTVS_INTEGRATOR_VIEWER = 'adminDashboard.totvsIntegratorViewer';

/** Papéis que veem o Integrador TOTVS (menu + rotas). SYSTEM_ADMINISTRATOR é curinga. */
export const TOTVS_INTEGRATOR_ROLES: Role[] = [
  ADMINISTRATOR,
  TOTVS_INTEGRATOR_ADMIN,
  TOTVS_INTEGRATOR_VIEWER,
];

/** Papéis que podem EXECUTAR ações no Integrador TOTVS (o viewer não entra). */
export const TOTVS_INTEGRATOR_WRITE_ROLES: Role[] = [
  ADMINISTRATOR,
  TOTVS_INTEGRATOR_ADMIN,
];

/**
 * Papéis que veem o Content Hub (menu + rotas). SYSTEM_ADMINISTRATOR é curinga.
 *
 * Houve um eixo `contentEditor` × `contentViewer` (migração
 * `20260724120000-seed-content-roles`), removido do catálogo em
 * `20260904120000-remove-unused-admin-roles` por nunca ter tido portador —
 * quem opera o Content Hub tem um dos dois admins, que já passavam em todo
 * `@Roles` do backend. Ler e escrever conteúdo têm hoje a mesma alçada; a
 * exceção continua sendo a taxonomia (ver `SUBJECT_WRITE_ROLES`).
 */
export const CONTENT_ROLES: Role[] = [ADMINISTRATOR];

/**
 * Ações sobre subjects (taxonomia) — caminho crítico, restrito a
 * SYSTEM_ADMINISTRATOR mesmo para quem tem CONTENT_EDITOR ou ADMINISTRATOR.
 * Espelha `StrictRoles('adminDashboard.systemAdministrator')` nos controllers
 * de subject/subject-edge/subject-view/subject-view-membership no backend.
 */
export const SUBJECT_WRITE_ROLES: Role[] = [SYSTEM_ADMINISTRATOR];

/**
 * Papéis que veem o Catálogo (menu + rotas). SYSTEM_ADMINISTRATOR é curinga.
 *
 * Houve um eixo `catalogViewer` × `catalogProductEditor` (migração
 * `20260724130000-seed-catalog-roles`), removido do catálogo em
 * `20260904120000-remove-unused-admin-roles` por nunca ter tido portador. Toda
 * aba do catálogo — inclusive as mutações de Perk/Role/Feature, que já eram só
 * de ADMINISTRATOR — está hoje sob a mesma alçada, então não há mais um gate
 * de escrita separado dentro das telas.
 */
export const CATALOG_ROLES: Role[] = [ADMINISTRATOR];

/**
 * Papéis que veem as telas de Vendas (menu + rotas), e também os que executam
 * as ações (cancelar BA, renegociar, abandonar contrato, reprocessar webhook,
 * baixa manual, anular lançamento, fechar inconsistência).
 *
 * Houve um eixo `salesViewer` × `salesEditor` (migração
 * `20260824130000-seed-sales-roles`), espelhando o par de controllers do módulo
 * `sales`; as duas roles foram removidas do catálogo em
 * `20260904120000-remove-unused-admin-roles` por nunca terem tido portador.
 * Leitura e escrita ficaram com a mesma alçada, e por isso não há um
 * `SALES_WRITE_ROLES` à parte — quem abre a tela pode agir nela.
 */
export const SALES_ROLES: Role[] = [ADMINISTRATOR];

/**
 * Campanhas de garantia do MedQ — espelha o `@Roles()` vazio do
 * `GuaranteeCampaignAdminController`, que só deixa passar os curingas.
 * `SYSTEM_ADMINISTRATOR` passa pelo curinga de `hasAnyRole`.
 */
export const GUARANTEE_ROLES: Role[] = [ADMINISTRATOR];

/**
 * Gerenciador de arquivos — só SYSTEM_ADMINISTRATOR vê a aba e as rotas
 * (Ruan, 2026-10-28). O monolith (`FILES_ADMIN_VIEW_ROLES` e
 * `FILES_ADMIN_EDIT_ROLES` em `src/globals/files/files-roles.ts`) ainda aceita
 * `administrator`; aqui o front é mais estrito que o back. Quando as roles por
 * bucket/pasta chegarem, entram aqui junto.
 */
export const FILES_ROLES: Role[] = [SYSTEM_ADMINISTRATOR];

/**
 * Papel que autoriza "atuar como" (impersonate/act-as) outro usuário. Não é um
 * papel de acesso a módulo — não entra em `KNOWN_ROLES` (não faz sentido no
 * seletor de "visualizar como"). SYSTEM_ADMINISTRATOR também autoriza (curinga).
 */
/**
 * Papéis que operam a fila de pendências do **Identity** (Central de
 * Pendências) — espelha `IDENTITY_ADMIN_ROLES` do monolith
 * (`src/globals/identity/identity-roles.ts`), que gateia
 * `/admin/identity/operational-alerts`. `CUSTOMER_SUCCESS_TEAM` fica **fora**
 * de propósito: ele alcança a listagem de usuários, não as rotas de alerta —
 * incluí-lo aqui faria a Central buscar e tomar 403. `SYSTEM_ADMINISTRATOR`
 * autoriza via curinga em `hasAnyRole`, então não precisa da linha.
 */
export const IDENTITY_ALERT_ROLES: Role[] = [ADMINISTRATOR];

/**
 * Mascara só no aristo-medq (o monolith recusa outro destino); o
 * admin-dashboard, onde o "Atuar como" de hoje abre a sessão, é só de
 * ADMINISTRATOR/SYSTEM_ADMINISTRATOR. Ver `use-can-impersonate.ts`.
 */
export const IMPERSONATOR = 'adminDashboard.impersonator';

/**
 * Papéis que executam as **ações administrativas sobre a pessoa** na página de
 * detalhe do usuário: editar cadastro, criar/ativar/desativar perfil de
 * plataforma e as operações de credencial (reset de senha, senha temporária,
 * convite, verificação, desvincular método de login).
 *
 * Deliberadamente **sem** `IAM_EDITOR`: ele abre essa página (é onde mora o
 * modal de acessos), mas no monolith só alcança os `@Get` de `/admin/identity`
 * (via `identity.viewer`) e as rotas de concessão de perk — todo o resto segue
 * no `adminDashboard.administrator` da classe do `IdentityAdminController`, e
 * as de credencial ainda passam pela escada de autoridade
 * (`.claude/rules/account-credentials.md`). Sem esta lista os botões
 * apareceriam para ele e devolveriam 403: não é buraco de segurança, é botão
 * que mente.
 */
export const USER_ADMIN_ACTION_ROLES: Role[] = [ADMINISTRATOR];

export type Role =
  | typeof SYSTEM_ADMINISTRATOR
  | typeof ADMINISTRATOR
  | typeof CUSTOMER_SUCCESS_TEAM
  | typeof IAM_EDITOR
  | typeof TOTVS_INTEGRATOR_ADMIN
  | typeof TOTVS_INTEGRATOR_VIEWER;

/** Super-admin técnico: acessa tudo, satisfaz qualquer checagem de papel. */
export const GENERAL_ADMIN_ROLE = SYSTEM_ADMINISTRATOR;

/** Papéis conhecidos (para o seletor de "visualizar como" e afins). */
export const KNOWN_ROLES: Role[] = [
  SYSTEM_ADMINISTRATOR,
  ADMINISTRATOR,
  CUSTOMER_SUCCESS_TEAM,
  IAM_EDITOR,
  TOTVS_INTEGRATOR_ADMIN,
  TOTVS_INTEGRATOR_VIEWER,
];

/** Rótulos amigáveis dos papéis conhecidos. */
export const ROLE_LABELS: Record<Role, string> = {
  [SYSTEM_ADMINISTRATOR]: 'Super admin',
  [ADMINISTRATOR]: 'Administrador',
  [CUSTOMER_SUCCESS_TEAM]: 'Customer Success',
  [IAM_EDITOR]: 'Editor de acessos',
  [TOTVS_INTEGRATOR_ADMIN]: 'Integrador TOTVS — Admin',
  [TOTVS_INTEGRATOR_VIEWER]: 'Integrador TOTVS — Visualizador',
};

/**
 * Verdadeiro se o usuário tem o papel exigido OU é o super-admin técnico
 * (SYSTEM_ADMINISTRATOR, curinga). Comparação por `perkKey` (string). É só UX
 * — o backend é a autoridade real de autorização.
 */
export function hasRole(
  userRoles: readonly string[],
  required: string,
): boolean {
  return userRoles.includes(GENERAL_ADMIN_ROLE) || userRoles.includes(required);
}

/**
 * Verdadeiro se o usuário satisfaz **qualquer um** dos papéis exigidos (admin
 * geral é curinga). Lista vazia = sem restrição (liberado a todos). Usado para
 * gatear um módulo/rota que aceita um conjunto de papéis.
 */
export function hasAnyRole(
  userRoles: readonly string[],
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  return required.some((role) => hasRole(userRoles, role));
}
